import chroma from 'chroma-js';
import mapboxgl from 'mapbox-gl';

import {SiteType} from 'src/lib/api/project';
import COLORS from 'src/lib/colors';

import {
  HOVERED_FEATURE_STATE_KEY,
  SELECTED_CONTRIBUTING_AREA_STATE_KEY,
  SELECTED_SITE_STATE_KEY,
} from './geoVizMapIds';

export const FLOWLINES_STRAHLER_NUM = 'streamorde';
const FLOWLINES_STRAHLER_MIN = 1;
const FLOWLINES_STRAHLER_MAX = 12;

function makeInterpolatedSize(min: number, max: number): mapboxgl.Expression {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    // zoom is 5 (or less) -> min
    5,
    min,
    // zoom is 18 (or greater) -> max
    18,
    max,
  ];
}

function makeCircleColors(
  selectedColor: string,
  hoveredColor: string,
  deselectedColor: string
): {[key: string]: mapboxgl.Expression | number} {
  return {
    'circle-stroke-width': 2,
    'circle-stroke-color': [
      'case',
      ['boolean', ['feature-state', SELECTED_SITE_STATE_KEY], false],
      chroma(selectedColor).darken(2).hex(),
      chroma(deselectedColor).darken(2).hex(),
    ],
    'circle-color': [
      'case',
      ['boolean', ['feature-state', SELECTED_SITE_STATE_KEY], false],
      selectedColor,
      ['boolean', ['feature-state', HOVERED_FEATURE_STATE_KEY], false],
      hoveredColor,
      deselectedColor,
    ],
  };
}

/*
Layer styling is static, so it's built once here: react-map-gl deep-compares
each Layer's paint/layout/filter props on every render, and stable references
short-circuit that to a pointer check.
*/
export const FLOWLINES_PAINT: mapboxgl.LinePaint = {
  'line-color': [
    'interpolate',
    ['linear'],
    ['get', FLOWLINES_STRAHLER_NUM],
    FLOWLINES_STRAHLER_MIN,
    COLORS.FLOWLINE_MIN,
    FLOWLINES_STRAHLER_MAX,
    COLORS.FLOWLINE_MAX,
  ],
  'line-width': [
    'interpolate',
    ['linear'],
    ['get', FLOWLINES_STRAHLER_NUM],
    FLOWLINES_STRAHLER_MIN,
    1,
    FLOWLINES_STRAHLER_MAX,
    5,
  ],
};

export const BASIN_SITE_TYPES_FILTER = [
  'in',
  ['get', 'siteType'],
  ['literal', [SiteType.BASIN, SiteType.SUBBASIN]],
];

export const BASIN_FILL_PAINT: mapboxgl.FillPaint = {
  'fill-color': [
    'case',
    ['boolean', ['feature-state', SELECTED_SITE_STATE_KEY], false],
    COLORS.BASIN_SELECTED_FILL,
    ['boolean', ['feature-state', HOVERED_FEATURE_STATE_KEY], false],
    COLORS.BASIN_HOVERED_FILL,
    COLORS.BASIN_DESELECTED_FILL,
  ],
  'fill-opacity': 0.4,
};

export const BASIN_STROKE_DESELECTED_PAINT: mapboxgl.LinePaint = {
  'line-width': 2,
  'line-color': COLORS.BASIN_DESELECTED_STROKE,
};

export const BASIN_STROKE_SELECTED_PAINT: mapboxgl.LinePaint = {
  'line-width': 2,
  'line-color': [
    'case',
    ['boolean', ['feature-state', SELECTED_SITE_STATE_KEY], false],
    COLORS.BASIN_SELECTED_STROKE,
    'transparent',
  ],
};

export const CONTRIBUTING_AREA_STROKE_PAINT: mapboxgl.LinePaint = {
  'line-width': 2,
  'line-color': [
    'case',
    ['boolean', ['feature-state', SELECTED_CONTRIBUTING_AREA_STATE_KEY], false],
    COLORS.CONTRIBUTING_AREA_STROKE,
    'transparent',
  ],
};

export const CONTRIBUTING_AREA_EDGE_STROKE_PAINT: mapboxgl.LinePaint = {
  'line-width': 2,
  'line-color': COLORS.CONTRIBUTING_AREA_EDGE_STROKE,
};

export const BASIN_POINT_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': makeInterpolatedSize(4, 10),
  ...makeCircleColors(
    COLORS.BASIN_POINT_SELECTED,
    COLORS.BASIN_POINT_HOVERED,
    COLORS.BASIN_POINT_DESELECTED
  ),
};

export const BASIN_ROUTED_POINT_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': makeInterpolatedSize(4, 10),
  ...makeCircleColors(
    COLORS.BASIN_ROUTED_POINT_SELECTED,
    COLORS.BASIN_ROUTED_POINT_HOVERED,
    COLORS.BASIN_ROUTED_POINT_DESELECTED
  ),
};

export const LOCATION_POINT_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': makeInterpolatedSize(3, 8),
  ...makeCircleColors(
    COLORS.LOCATION_POINT_SELECTED,
    COLORS.LOCATION_POINT_HOVERED,
    COLORS.LOCATION_POINT_DESELECTED
  ),
};

export const GAUGE_POINT_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': makeInterpolatedSize(4, 10),
  ...makeCircleColors(
    COLORS.GAUGE_POINT_SELECTED,
    COLORS.GAUGE_POINT_HOVERED,
    COLORS.GAUGE_POINT_DESELECTED
  ),
};

export const ACTIVE_POINT_LABEL_LAYOUT: mapboxgl.SymbolLayout = {
  'text-field': ['get', 'siteName'],
  'text-size': 12,
  'text-justify': 'auto',
  'text-allow-overlap': true,
  'text-radial-offset': 1,
  'text-variable-anchor': ['left', 'right'],
};

export const ACTIVE_POINT_LABEL_PAINT: mapboxgl.SymbolPaint = {
  'text-opacity': [
    'case',
    ['boolean', ['feature-state', SELECTED_SITE_STATE_KEY], false],
    1,
    ['boolean', ['feature-state', HOVERED_FEATURE_STATE_KEY], false],
    0.5,
    0,
  ],
};
